import { useEffect, useState } from "react";
import { Shield, Lock, Mail, Server, Eye, FileWarning, Ban, CheckCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import ScannerForm from "@/components/ScannerForm";
import ScanHistory from "@/components/ScanHistory";
import { loadHistory } from "@/lib/history";

const CHECKS = [
  { icon: Lock, name: "HTTPS / TLS", desc: "Verifies the site is reachable over encrypted TLS." },
  { icon: Shield, name: "Certificate Status", desc: "Reads the leaf cert, issuer, and days until expiry." },
  { icon: Server, name: "HSTS", desc: "Checks Strict-Transport-Security and its max-age strength." },
  { icon: FileWarning, name: "Content-Security-Policy", desc: "Flags missing CSP or wildcard / unsafe-inline weakenings." },
  { icon: Eye, name: "X-Content-Type-Options", desc: "Confirms nosniff is set to block MIME confusion attacks." },
  { icon: Ban, name: "Clickjacking", desc: "Looks for X-Frame-Options or CSP frame-ancestors coverage." },
  { icon: Mail, name: "SPF", desc: "Resolves the apex TXT record and inspects the all qualifier." },
  { icon: CheckCheck, name: "DMARC", desc: "Resolves _dmarc TXT and reads the enforcement policy." },
];

const SAFETY_RULES = [
  "No exploitation of vulnerabilities.",
  "No password guessing or brute force.",
  "No file uploads to the target.",
  "No destructive testing.",
  "Localhost and private networks are blocked.",
  "Ownership or written authorization is required before active checks.",
  "We never claim a site is unhackable.",
];

export default function Landing() {
  const [history, setHistory] = useState([]);
  useEffect(() => setHistory(loadHistory()), []);

  return (
    <div className="min-h-screen pb-20" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/60 to-slate-950 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-cyan-300">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 pulse-dot" />
              Zero-cost passive audit for small businesses
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Instant cyber defense{" "}
              <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                for your domain.
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Scan HTTPS, TLS, HSTS, CSP, clickjacking protection, SPF and DMARC in seconds.
              No exploits. No guessing. Just plain-English findings with the exact fix to paste
              into your server config.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2"><CheckCheck className="h-4 w-4 text-emerald-400" /> 8 non-destructive checks</li>
              <li className="flex items-center gap-2"><CheckCheck className="h-4 w-4 text-emerald-400" /> 0-100 security score</li>
              <li className="flex items-center gap-2"><CheckCheck className="h-4 w-4 text-emerald-400" /> Copy-paste fixes</li>
              <li className="flex items-center gap-2"><CheckCheck className="h-4 w-4 text-emerald-400" /> Local scan history</li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <ScannerForm />
          </div>
        </div>
      </section>

      {/* CHECKS */}
      <section id="checks" className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-cyan-400 font-bold">
                What we check
              </div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">
                Eight safe, passive signals.
              </h2>
              <p className="mt-2 text-slate-400 max-w-2xl text-sm sm:text-base">
                Each check runs a single read-only lookup. No traffic is sent that a normal
                browser or mail server would not already send.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CHECKS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.name}
                  className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-cyan-500/30 hover:bg-slate-900 transition-colors"
                  data-testid={`check-tile-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10 border border-cyan-500/20 mb-4">
                    <Icon className="h-4.5 w-4.5 text-cyan-300" />
                  </div>
                  <h3 className="font-display font-semibold text-slate-100">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SAFETY */}
      <section id="safety" className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-widest text-cyan-400 font-bold">
              Our safety rules
            </div>
            <h2 className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">
              Passive by design.
            </h2>
            <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed">
              ShieldPilot is built for site owners, not pentesters. Every check is read-only
              and runs with the same privileges as any ordinary HTTPS request.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SAFETY_RULES.map((r) => (
                <li
                  key={r}
                  className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4"
                >
                  <Shield className="h-4.5 w-4.5 text-cyan-300 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      {history.length > 0 && (
        <section className="relative py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScanHistory history={history} onChange={setHistory} compact />
          </div>
        </section>
      )}

      <footer className="mt-10 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Shield className="h-3.5 w-3.5 text-cyan-400" />
            ShieldPilot · Passive security audit · Free tier
          </div>
          <div className="font-mono">No single tool can prove a site is unhackable.</div>
        </div>
      </footer>
    </div>
  );
}
